import type { ReactNode } from 'react';

import { Icon } from './Icon';

/** Which way a column is sorted. */
export type SortDirection = 'ascending' | 'descending';

/** The active sort, or null when the table is in its natural order. */
export interface SortState {
  readonly columnId: string;
  readonly direction: SortDirection;
}

/** One column of a {@link DataTable}. */
export interface DataTableColumn<Row> {
  /** Stable identifier, used as the React key and in {@link SortState}. */
  readonly id: string;
  /** The column heading. */
  readonly header: ReactNode;
  /** Renders one cell. */
  readonly cell: (row: Row) => ReactNode;
  /** Whether the header offers a sort control. */
  readonly sortable?: boolean;
  /** Right-align numeric columns; money and durations read better that way. */
  readonly align?: 'start' | 'end';
  /** A CSS width for the column, e.g. `12rem` or `minmax(8rem, 1fr)`. */
  readonly width?: string;
}

export interface DataTableProps<Row> {
  /** What the table is; rendered as the caption unless `captionHidden`. */
  readonly caption: string;
  /** Hides the caption visually while keeping it for assistive technology. */
  readonly captionHidden?: boolean;
  /** The columns, in display order. */
  readonly columns: readonly DataTableColumn<Row>[];
  /** The rows, already filtered, sorted and paged by the caller. */
  readonly rows: readonly Row[];
  /** Stable key for a row. */
  readonly rowKey: (row: Row) => string;
  /** The active sort, for the `aria-sort` state on the headers. */
  readonly sort?: SortState | null;
  /** Called when a sortable header is activated. */
  readonly onSortChange?: (sort: SortState) => void;
  /** Shown in place of the body when there are no rows. */
  readonly empty?: ReactNode;
  /** Marks a row as the current selection. */
  readonly isRowCurrent?: (row: Row) => boolean;
}

/**
 * A data table.
 *
 * Sorting is controlled and stateless here: the component renders the sort it
 * is given and reports the sort the user asked for. A table that sorted its own
 * copy of the data would immediately be wrong for every screen in this product,
 * because the dispatch board, the pipeline and the invoice ledger all sort on
 * the server against more rows than the client holds.
 *
 * The markup is a real `<table>` with a real `<caption>`, `<th scope>` on every
 * header and `aria-sort` on the sorted one. It is virtualisation-ready in the
 * sense that matters — the caller owns the row window — without shipping a
 * virtualiser before there is a screen that needs one.
 *
 * @param props see {@link DataTableProps}
 * @returns the table
 */
export function DataTable<Row>({
  caption,
  captionHidden = false,
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  empty,
  isRowCurrent,
}: DataTableProps<Row>): React.JSX.Element {
  function nextSort(columnId: string): SortState {
    if (sort?.columnId === columnId && sort.direction === 'ascending') {
      return { columnId, direction: 'descending' };
    }
    return { columnId, direction: 'ascending' };
  }

  return (
    <div className="fo-table__scroll" tabIndex={0} role="region" aria-label={caption}>
      <table className="fo-table">
        <caption className={captionHidden ? 'visually-hidden' : 'fo-table__caption'}>
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const sorted = sort?.columnId === column.id ? sort.direction : undefined;
              return (
                <th
                  key={column.id}
                  scope="col"
                  data-align={column.align ?? 'start'}
                  style={column.width === undefined ? undefined : { width: column.width }}
                  aria-sort={column.sortable === true ? (sorted ?? 'none') : undefined}
                >
                  {column.sortable === true && onSortChange !== undefined ? (
                    <button
                      type="button"
                      className="fo-table__sort"
                      onClick={() => {
                        onSortChange(nextSort(column.id));
                      }}
                    >
                      <span>{column.header}</span>
                      <Icon
                        name={sorted === 'descending' ? 'arrow-down' : 'arrow-up'}
                        className="fo-table__sort-icon"
                        size="1em"
                      />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="fo-table__empty" colSpan={columns.length}>
                {empty ?? 'Nothing to show.'}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                data-current={isRowCurrent?.(row) === true ? '' : undefined}
                aria-current={isRowCurrent?.(row) === true ? 'true' : undefined}
              >
                {columns.map((column) => (
                  <td key={column.id} data-align={column.align ?? 'start'}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
